/**
 * CodeEditorEditor
 * Configuration editor for code-editor control
 * Handles language, syntax, and theme settings
 */

import React, { useCallback } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';

interface CodeEditorConfig {
  language?: string;
  theme?: string;
  lineNumbers?: boolean;
  readOnly?: boolean;
  tabSize?: number;
  minHeight?: number;
  maxHeight?: number;
  syntaxHighlight?: boolean;
}

const SUPPORTED_LANGUAGES = [
  'javascript',
  'typescript',
  'python',
  'java',
  'csharp',
  'cpp',
  'go',
  'rust',
  'html',
  'css',
  'json',
  'xml',
  'sql',
  'bash',
];

function renderCodeEditorEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as CodeEditorConfig;

  const language = config.language || 'javascript';
  const theme = config.theme || 'default';
  const lineNumbers = config.lineNumbers ?? true;
  const readOnly = config.readOnly ?? false;
  const tabSize = config.tabSize ?? 2;
  const minHeight = config.minHeight ?? 200;
  const maxHeight = config.maxHeight ?? 600;
  const syntaxHighlight = config.syntaxHighlight ?? true;

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Code Editor Configuration</h4>

      <div className="af-properties-tab__group">
        <label htmlFor="language-select" className="af-properties-tab__label">
          Programming Language
        </label>
        <select
          id="language-select"
          value={language}
          onChange={(e) => onConfigChange('language', e.target.value)}
          className="af-properties-tab__select"
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <option key={lang} value={lang}>
              {lang.charAt(0).toUpperCase() + lang.slice(1)}
            </option>
          ))}
        </select>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="theme-select" className="af-properties-tab__label">
          Theme
        </label>
        <select
          id="theme-select"
          value={theme}
          onChange={(e) => onConfigChange('theme', e.target.value)}
          className="af-properties-tab__select"
        >
          <option value="default">Default</option>
          <option value="dark">Dark</option>
          <option value="monokai">Monokai</option>
          <option value="github">GitHub</option>
          <option value="twilight">Twilight</option>
          <option value="xcode">Xcode</option>
        </select>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={lineNumbers}
            onChange={(e) => onConfigChange('lineNumbers', e.target.checked)}
          />
          Show Line Numbers
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={syntaxHighlight}
            onChange={(e) => onConfigChange('syntaxHighlight', e.target.checked)}
          />
          Enable Syntax Highlighting
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={readOnly}
            onChange={(e) => onConfigChange('readOnly', e.target.checked)}
          />
          Read-Only Mode
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="tab-size-input" className="af-properties-tab__label">
          Tab Size (spaces)
        </label>
        <input
          id="tab-size-input"
          type="number"
          value={tabSize}
          onChange={(e) => onConfigChange('tabSize', Number(e.target.value))}
          min="1"
          max="8"
          className="af-properties-tab__input"
          aria-label="Tab size"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="min-height-input" className="af-properties-tab__label">
          Minimum Height (pixels)
        </label>
        <input
          id="min-height-input"
          type="number"
          value={minHeight}
          onChange={(e) => onConfigChange('minHeight', Number(e.target.value))}
          min="50"
          className="af-properties-tab__input"
          aria-label="Minimum height"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="max-height-input" className="af-properties-tab__label">
          Maximum Height (pixels, 0 = unlimited)
        </label>
        <input
          id="max-height-input"
          type="number"
          value={maxHeight}
          onChange={(e) => onConfigChange('maxHeight', Number(e.target.value))}
          min="0"
          className="af-properties-tab__input"
          aria-label="Maximum height"
        />
      </div>
    </div>
  );
}

export const CodeEditorEditor: PropertyEditor = {
  type: 'code-editor',
  label: 'Code Editor',
  description: 'Configure code editor with language, syntax, and theming',
  render: renderCodeEditorEditor,
};
